import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  MessageSquare, 
  Bot, 
  Activity, 
  TrendingUp, 
  TrendingDown,
  Clock,
  Zap,
  BarChart3,
  PieChart as PieChartIcon,
  ArrowUpRight,
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/dateFormat';
import { formatCompactNumber } from '../../utils/formatNumber';
import PageTitle from '../../components/PageTitle';

interface AnalyticsData {
  overview: {
    totalUsers: number;
    totalConversations: number;
    totalMessages: number;
  };
  conversationStats?: Array<{
    _id: string;
    count: number;
  }>;
  comparison?: {
    totalUsers: number;
    totalConversations: number;
    totalMessages: number;
    modelsUsed: number;
    avgMessages: number;
    avgConversations: number;
  };
  engagementRate?: number;
  recentUsers?: Array<{
    id: string;
    email: string;
    verified: boolean;
    create_at: string;
  }>;
}

const SOFT_ACCENT_COLORS = [
  { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-600 dark:text-blue-400', value: 'text-blue-700 dark:text-blue-300' },
  { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-600 dark:text-emerald-400', value: 'text-emerald-700 dark:text-emerald-300' },
  { bg: 'bg-violet-50 dark:bg-violet-950/30', text: 'text-violet-600 dark:text-violet-400', value: 'text-violet-700 dark:text-violet-300' },
  { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-600 dark:text-amber-400', value: 'text-amber-700 dark:text-amber-300' },
];

const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

function DashboardAnalytics() {
  const { t } = useTranslation('dashboard');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/admin/stats', { params: { range: timeRange } });
      setData(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error(t('analytics.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    if (!data) return null;

    const totalConversations = data.overview.totalConversations || 0;
    const totalMessages = data.overview.totalMessages || 0;
    const totalUsers = data.overview.totalUsers || 0;

    const avgMessagesPerConversation = totalConversations > 0 
      ? (totalMessages / totalConversations).toFixed(1) 
      : '0';
    
    const avgConversationsPerUser = totalUsers > 0 
      ? (totalConversations / totalUsers).toFixed(1) 
      : '0';

    const modelUsage = data.conversationStats || [];
    const totalModelUsage = modelUsage.reduce((sum, m) => sum + m.count, 0);
    const topModel = modelUsage.length > 0 ? modelUsage[0] : null;

    return {
      avgMessagesPerConversation,
      avgConversationsPerUser,
      modelUsage,
      totalModelUsage,
      topModel,
    };
  };

  const metrics = calculateMetrics();
  const comparison = data?.comparison;
  const fmtPct = (v: number | undefined) => `${(v ?? 0) >= 0 ? '+' : ''}${(v ?? 0).toFixed(1)}%`;
  const fmtDelta = (v: number | undefined) => `${(v ?? 0) >= 0 ? '+' : ''}${v ?? 0}`;
  const trendOf = (v: number | undefined): 'up' | 'down' => ((v ?? 0) >= 0 ? 'up' : 'down');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <PageTitle titleKey="analytics.title" namespace="dashboard" descriptionKey="analytics.subtitle" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">{t('analytics.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('analytics.subtitle')}</p>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                timeRange === range
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {range === '7d' ? t('analytics.days_7') : range === '30d' ? t('analytics.days_30') : t('analytics.days_90')}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={t('analytics.total_users')}
          value={formatCompactNumber(data?.overview.totalUsers || 0)}
          change={fmtPct(comparison?.totalUsers)}
          trend={trendOf(comparison?.totalUsers)}
          icon={Users}
          accent={SOFT_ACCENT_COLORS[0]}
        />
        <MetricCard
          title={t('analytics.total_conversations')}
          value={formatCompactNumber(data?.overview.totalConversations || 0)}
          change={fmtPct(comparison?.totalConversations)}
          trend={trendOf(comparison?.totalConversations)}
          icon={MessageSquare}
          accent={SOFT_ACCENT_COLORS[1]}
        />
        <MetricCard
          title={t('analytics.total_messages')}
          value={formatCompactNumber(data?.overview.totalMessages || 0)}
          change={fmtPct(comparison?.totalMessages)}
          trend={trendOf(comparison?.totalMessages)}
          icon={Activity}
          accent={SOFT_ACCENT_COLORS[3]}
        />
        <MetricCard
          title={t('analytics.models_used')}
          value={data?.conversationStats?.length?.toString() || '0'}
          change={fmtDelta(comparison?.modelsUsed)}
          trend={trendOf(comparison?.modelsUsed)}
          icon={Bot}
          accent={SOFT_ACCENT_COLORS[2]}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('analytics.avg_messages')}</h3>
            <BarChart3 className="h-4 w-4 text-muted-foreground/60" />
          </div>
          <p className="text-2xl font-bold text-foreground">{metrics?.avgMessagesPerConversation || '0'}</p>
          <p className={`text-xs mt-2 flex items-center ${trendOf(comparison?.avgMessages) === 'up' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
            {trendOf(comparison?.avgMessages) === 'up' ? <ArrowUpRight className="h-3.5 w-3.5 mr-0.5" /> : <TrendingDown className="h-3.5 w-3.5 mr-0.5" />}
            {t('analytics.from_last', { percent: fmtPct(comparison?.avgMessages) })}
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('analytics.avg_conversations')}</h3>
            <PieChartIcon className="h-4 w-4 text-muted-foreground/60" />
          </div>
          <p className="text-2xl font-bold text-foreground">{metrics?.avgConversationsPerUser || '0'}</p>
          <p className={`text-xs mt-2 flex items-center ${trendOf(comparison?.avgConversations) === 'up' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
            {trendOf(comparison?.avgConversations) === 'up' ? <ArrowUpRight className="h-3.5 w-3.5 mr-0.5" /> : <TrendingDown className="h-3.5 w-3.5 mr-0.5" />}
            {t('analytics.from_last', { percent: fmtPct(comparison?.avgConversations) })}
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('analytics.top_model')}</h3>
            <Zap className="h-4 w-4 text-muted-foreground/60" />
          </div>
          <p className="text-base font-bold text-foreground truncate" title={metrics?.topModel?._id}>
            {metrics?.topModel?._id || '—'}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {t('analytics.conversations_count', { count: metrics?.topModel?.count || 0 })}
          </p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Model Usage Distribution */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-5 flex items-center gap-2">
            <Bot className="h-4 w-4 text-muted-foreground" />
            {t('analytics.model_distribution')}
          </h3>
          <div className="space-y-3.5">
            {(data?.conversationStats || []).slice(0, 8).map((stat, index) => {
              const percentage = metrics?.totalModelUsage 
                ? ((stat.count / metrics.totalModelUsage) * 100).toFixed(1)
                : '0';
              return (
                <div key={stat._id} className="flex items-center gap-3">
                  <div 
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-foreground truncate" title={stat._id}>
                        {stat._id}
                      </span>
                      <span className="text-xs font-medium text-muted-foreground ml-2">
                        {stat.count} ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full transition-all duration-500"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: CHART_COLORS[index % CHART_COLORS.length]
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            {(!data?.conversationStats || data.conversationStats.length === 0) && (
              <p className="text-center text-muted-foreground py-8 text-sm">
                {t('analytics.no_model_data')}
              </p>
            )}
          </div>
        </div>

        {/* Usage Activity */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-5 flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            {t('analytics.activity_overview')}
          </h3>
          
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Users, label: t('analytics.users_label'), value: formatCompactNumber(data?.overview.totalUsers || 0), accent: SOFT_ACCENT_COLORS[0] },
                { icon: MessageSquare, label: t('analytics.chats_label'), value: formatCompactNumber(data?.overview.totalConversations || 0), accent: SOFT_ACCENT_COLORS[1] },
                { icon: Zap, label: t('analytics.messages_label'), value: formatCompactNumber(data?.overview.totalMessages || 0), accent: SOFT_ACCENT_COLORS[2] },
                { icon: Bot, label: t('analytics.models_label'), value: String(data?.conversationStats?.length || 0), accent: SOFT_ACCENT_COLORS[3] },
              ].map((item) => (
                <div key={item.label} className={`${item.accent.bg} rounded-lg p-3.5`}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <item.icon className={`h-3.5 w-3.5 ${item.accent.text}`} />
                    <span className={`text-xs ${item.accent.text}`}>{item.label}</span>
                  </div>
                  <p className={`text-xl font-bold ${item.accent.value}`}>{item.value}</p>
                </div>
              ))}
            </div>

            {/* Engagement Rate */}
            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{t('analytics.engagement_rate')}</span>
                <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  {(data?.engagementRate ?? 0).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-emerald-500/70 dark:bg-emerald-500/50 transition-all duration-500"
                  style={{ width: `${data?.engagementRate ?? 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Users Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            {t('analytics.recent_users')}
          </h3>
          <span className="text-xs text-muted-foreground">
            {t('analytics.last_period', { period: timeRange === '7d' ? t('analytics.days_7') : timeRange === '30d' ? t('analytics.days_30') : t('analytics.days_90') })}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('analytics.col_user')}
                </th>
                <th className="text-left px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('analytics.col_joined')}
                </th>
                <th className="text-left px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('analytics.col_status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(data?.recentUsers || []).slice(0, 10).map((user) => (
                <tr key={user.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-primary">
                          {(user.email || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-foreground">{user.email}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 whitespace-nowrap text-sm text-muted-foreground">
                    {formatDate(user.create_at)}
                  </td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    {user.verified ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                        {t('analytics.status_verified')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {t('analytics.status_unverified')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {(!data?.recentUsers || data.recentUsers.length === 0) && (
                <tr>
                  <td colSpan={3} className="px-5 py-8 text-center text-sm text-muted-foreground">
                    {t('analytics.no_recent_users')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ 
  title, 
  value, 
  change, 
  trend, 
  icon: Icon, 
  accent 
}: {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  icon: any;
  accent: { bg: string; text: string; value: string };
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold text-foreground mt-1.5">{value}</p>
          <div className={`flex items-center mt-1.5 text-xs ${
            trend === 'up' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
          }`}>
            {trend === 'up' ? (
              <TrendingUp className="h-3.5 w-3.5 mr-0.5" />
            ) : (
              <TrendingDown className="h-3.5 w-3.5 mr-0.5" />
            )}
            {change}
          </div>
        </div>
        <div className={`p-2.5 rounded-lg ${accent.bg}`}>
          <Icon className={`h-6 w-6 ${accent.text}`} />
        </div>
      </div>
    </div>
  );
}

export default DashboardAnalytics;
